// React bindings. `<I18nProvider i18n={...}>` threads an I18n instance down;
// `useTranslation()` returns bound `t`/`tc` + current locale.
import { createContext, createElement, useCallback, useContext, useSyncExternalStore } from 'react';
import type { ReactNode } from 'react';
import type { I18n, Locale } from '../types.js';

interface I18nContextValue {
  i18n: I18n;
}

const I18nContext = createContext<I18nContextValue | null>(null);
I18nContext.displayName = 'GovardI18nContext';

export interface I18nProviderProps {
  i18n: I18n;
  children?: ReactNode;
}

export function I18nProvider(props: I18nProviderProps): ReactNode {
  return createElement(I18nContext.Provider, { value: { i18n: props.i18n } }, props.children);
}

export interface UseTranslationResult {
  t: I18n['t'];
  tc: I18n['tc'];
  locale: Locale;
  setLocale: I18n['setLocale'];
}

export function useTranslation(): UseTranslationResult {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error('[@govard/i18n-core] useTranslation must be used within <I18nProvider>.');
  }
  const { i18n } = ctx;
  const locale = useSyncExternalStore(
    i18n.subscribe,
    () => i18n.locale,
    () => i18n.locale,
  );
  const t = useCallback<I18n['t']>((key, vars) => i18n.t(key, vars), [i18n, locale]);
  const tc = useCallback<I18n['tc']>(
    (key, count, vars) => i18n.tc(key, count, vars),
    [i18n, locale],
  );
  const setLocale = useCallback<I18n['setLocale']>((next) => i18n.setLocale(next), [i18n]);
  return {
    t,
    tc,
    locale,
    setLocale,
  };
}

export { I18nContext };
